<template>
  <transition
    :enterActiveClass="$style.dropDownMenuTransition"
    :enterClass="$style.dropDownMenuEnter"
    :leaveActiveClass="$style.dropDownMenuTransition"
    :leaveToClass="$style.dropDownMenuLeaveTo"
  >
    <ul
      v-if="showList"
      v-on-clickaway="handleClickaway"
      :class="[ getMenuListClassName, getMenuFontSize ]"
      role="menu"
    >
      <template v-for="(section, sectionIndex) in $props.menuSections">
        <li
          :key="section.menuLabel"
          :class="$style.menuSection"
        >
          <h5
            v-if="showMenuLabel"
            :class="$style.menuLabel"
          >
            {{ section.menuLabel }}
            <span
              v-if="section.menuType"
            >
              {{ `(${section.menuType})` }}
            </span>
          </h5>
          <menu :class="$style.menuItemsContainer">
            <template v-for="(item, index) in section.menuItems">
              <li
                v-if="item.addSeparator"
                :key="index"
                :class="$style.menuItemSeparator"
                role="presentation"
              />
              <li
                :key="setCustomKey(item)"
                role="presentation"
              >
                <component
                  :is="item.static ? 'span' : 'a'"
                  :class="[
                    $style.menuItem,
                    { [$style.selected]: itemsSelected[sectionIndex][index] },
                    { [$style.disabled]: item.disabled },
                    { [$style.includesIcon]: item.showExternalLinkIcon },
                    { [$style.rightAlignIcon]: item.rightAlignExternalLinkIcon },
                    { [$style.static]: item.static },
                  ]"
                  :data-test-id="`DropDownList__menuItem${sectionIndex}_${index}`"
                  :href="typeof item.action === 'string' ? item.action : 'javascript:void(0);'"
                  role="menuitem"
                  :tabindex="item.static ? undefined : '-1'"
                  :target="item.external ? '_blank' : '_self'"
                  @click="handleItemClick($event, item)"
                >
                  <div :class="{[$style.alignWithCustomIcon]: item.customIcon}">
                    <component
                      :is="item.component"
                      v-if="item.component"
                      v-bind="item.componentProps"
                      :class="$style.menuItemIcon"
                    />
                    <CustomIcon
                      v-if="item.customIcon"
                      data-test-id="DropDownList__customIcon"
                      :scale="1"
                    >
                      <component
                        :is="item.customIcon"
                        :class="$style.customIcon"
                      />
                    </CustomIcon>
                    <span
                      :class="$style.menuItemLabel"
                      :title="item.label"
                    >
                      {{ item.label }}
                    </span>
                    <small
                      v-if="item.subLabel"
                      :class="$style.menuItemSubLabel"
                    >
                      {{ item.subLabel }}
                    </small>
                    <strong
                      v-if="item.exampleText"
                      :class="$style.menuItemExampleText"
                    >
                      {{ ` ${item.exampleText}` }}
                    </strong>
                  </div>
                  <span
                    v-if="itemsSelected[sectionIndex][index]"
                    :class="$style.menuItemSelectedText"
                    data-test-id="DropDownList__menuItemSelectedText"
                  >
                    selected
                  </span>
                  <svg fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 8 8" v-if="item.showExternalLinkIcon" :class="$style.externalLinkIcon" v-bind:svg-inline="''" v-bind:role="'presentation'" v-bind:focusable="'false'" v-bind:tabindex="'-1'"><path fill-rule="evenodd" clip-rule="evenodd" d="M6.78 6.595a.667.667 0 00.672-.66l.043-4.757a.667.667 0 00-.673-.673L2.066.548a.667.667 0 00.012 1.333l3.126-.028L.7 6.357a.667.667 0 00.943.943l4.504-4.504-.028 3.126a.667.667 0 00.66.673z" fill="#176FAC"/></svg>
                  <NewBadge v-if="item.isNew" />
                </component>
              </li>
            </template>
          </menu>
        </li>
      </template>
    </ul>
  </transition>
</template>

<script>
import {
  CustomIcon,
} from '@jumpcloud/ui-components';
import { Sizes } from 'client/util/Constants';
import { UiEvents } from '@jumpcloud/ui-events-vue';
import { mixin as clickaway } from 'vue-clickaway';
import { itemTypePages } from 'client/GrowthEngineering/utilities/GrowthConstants';
import { mapActions, mapGetters } from 'vuex';
import DropDownMixin from 'client/components/DropDownMenu/DropDownMixin';
import NewBadge from 'client/components/Badges/NewBadge.vue';
import PageNavigationUtil from 'client/util/PageNavigationUtil';

export default {
  name: 'DropDownList',

  components: {
    CustomIcon,
    NewBadge,
  },

  mixins: [clickaway, DropDownMixin],

  props: {
    closeDropDown: {
      default: () => {},
      type: Function,
    },
    customKey: {
      type: Boolean,
      default: false,
    },
    itemSection: {
      type: String,
      default: () => {},
    },
    itemType: {
      type: String,
      default: '',
    },
    onItemClick: {
      default: () => {},
      type: Function,
    },
    showList: {
      type: Boolean,
      required: true,
    },
  },

  computed: {
    getMenuFontSize() {
      const size = Sizes[this.menuFontSize];
      const titleCase = size.charAt(0).toUpperCase()
        + size.slice(1);

      return this.$style[`menuFontSize${titleCase}`];
    },

    getMenuListClassName() {
      const classNames = [this.$style.dropDownMenuList];

      if (this.isLargeDropDown) {
        classNames.push(this.$style.dropDownMenuListLarge);
      }
      if (this.classNames && this.classNames.dropDownList) {
        classNames.push(this.$style[this.classNames.dropDownList]);
      }
      if (this.leftAlignList) {
        classNames.push(this.$style.leftAlignList);
      }
      if (this.displayMenuListOnTop) {
        classNames.push(this.$style.dropDownMenuListOnTop);
      }
      if (this.bottomAlign) {
        classNames.push(this.$style.bottomAligned);
      }
      return classNames;
    },

    itemsSelected() {
      return this.menuSections.map(
        menuSection => menuSection.menuItems.map((item) => this.isSelected(item)),
      );
    },
  },

  beforeCreate() {
    if (!this.$options.propsData.storeModule) return;

    this.$options.computed = {
      ...this.$options.computed,
      ...mapGetters(this.$options.propsData.storeModule, {
        filters: 'getAppliedFilters',
      }),
    };

    this.$options.methods = {
      ...this.$options.methods,
      ...mapActions(this.$options.propsData.storeModule, [
        'deleteFilter',
      ]),
    };
  },

  methods: {
    getItemFilter(item) {
      return (item.filter instanceof Array) ? item.filter[0] : item.filter;
    },

    handleClickaway() {
      // The filter dropdown should not close when selecting an option. This code will
      // close the list since there isn't a clickaway event on filter drop down anchor.
      return this.showList ? this.closeDropDown() : '';
    },

    handleItemClick($event, item) {
      UiEvents.triggerDropdownItemSelected({
        description: 'Selects an item from dropdown',
        page: itemTypePages[this.itemType] || this.$page,
        section: this.itemSection,
        text: item.label,
        toUrl: this.$route ? this.$route.path : PageNavigationUtil.getLocationHashPath(),
      });

      if (item.disabled || item.static) {
        $event.preventDefault();
        $event.stopImmediatePropagation();
        return;
      }

      if (this.isSelected(item)) {
        this.deleteFilter({ filterName: this.getItemFilter(item).name });
        this.refresh();
        return;
      }

      if (typeof item.action === 'function') {
        item.action();
      }

      this.onItemClick(item);
    },

    isSelected(item) {
      if (!this.filters || !this.filters.length) return false;
      if (!item.filter) return false;

      const itemFilter = this.getItemFilter(item);

      return this.filters.some(filter => (
        filter.type === itemFilter.type && filter.displayValue === itemFilter.displayValue
      ));
    },

    setCustomKey(item) {
      return this.customKey ? item.id : item.label;
    },
  },
};
</script>

<style scoped module>
@value ( max-width-medium, max-width-medium-large ) from '~css/breakpoints.css';
@value ( jcGrey800 ) from '~css/brand-colors.css';
@value (
  gradientDark0,
  gradientDark2,
  gradientDark4,
  gradientLight3,
  gradientLight4,
  tealLight1,
  tealLight2) from '~css/colors.css';
@value ( dropDownMenuTransition ) from '~css/transitions.css';
@value dropDownMenuZIndex from '~css/z-index.css';
@value dropDownMenuBottomSize from '~css/layout.css';

.dropDownMenuList {
  background-clip: padding-box;
  background-color: white;
  border: 0;
  border-radius: 3px;
  box-shadow: 0 1px 10px 1px rgba(0, 0, 0, 0.16);
  left: auto;
  list-style: none;
  margin: 0;
  max-height: 500px;
  max-width: 800px;
  min-width: 175px;
  overflow-x: hidden;
  overflow-y: auto;
  padding: 0;
  position: absolute;
  right: 0;
  text-align: left;
  top: 100%;
  width: min-content;
  z-index: dropDownMenuZIndex;
}

.alignWithCustomIcon {
  align-items: center;
  display: flex;
}

.bottomAligned {
  bottom: 0;
  top: auto;
}

.customIcon {
  margin-right: var(--jc-spacer-x-small);
}

.dropDownMenuList.dropDownMenuListLarge {
  min-width: 225px;
  padding: 20px 0;
}

.dropDownMenuEnter,
.dropDownMenuLeaveTo {
  opacity: 0;
}

.dropDownMenuTransition {
  transition: dropDownMenuTransition;
}

.dropDownMenuListOnTop {
  bottom: dropDownMenuBottomSize;
  top: unset;
}

.filterBarDropDownList {
  min-width: 220px;
}

.leftAlignList {
  left: 0;
}

.menuFontSizeSmall {
  font-size: 13px;
  font-weight: 400;
}

.menuFontSizeMedium {
  font-size: 15px;
  font-weight: 400;
}

.menuFontSizeLarge {
  font-size: 17px;
  font-weight: 400;
}

.menuItem.selected {
  background-color: gradientLight4;
  color: gradientDark0 !important;
}

.menuItem.selected:focus {
  background-color: gradientLight4 !important;
  color: gradientDark0 !important;
}

.menuItem.selected:hover {
  background-color: tealLight1 !important;
  color: jcGrey800 !important;
}

.menuItem {
  color: gradientDark2;
  cursor: pointer;
  display: flex;
  font-weight: 600;
  justify-content: space-between;
  line-height: 1.4;
  outline: none !important;
  padding: 5px 25px;
  text-decoration: none !important;
  white-space: nowrap;
}

.menuItem > div {
  display: flex;
}

.menuItem:active {
  color: jcGrey800;
}

.menuItem:focus {
  background-color: tealLight2 !important;
}

.menuItem.disabled {
  cursor: default;
  opacity: 0.5;
}

.menuItem.disabled:active,
.menuItem.disabled:focus,
.menuItem.disabled:hover {
  background-color: inherit;
  color: jcGrey800;
}

.menuItem.includesIcon {
  align-items: center;
  justify-content: flex-start;
}

.menuItem.rightAlignIcon {
  justify-content: space-between;
}

.menuItem.static {
  cursor: unset;
}

.filterBarDropDownList .menuItem {
  border-radius: 3px;
  margin: 3px;
}

.dropDownMenuListLarge .menuItem {
  padding: 5px 35px;
}

.menuItem:not(.static):hover {
  background-color: tealLight2 !important;
  color: jcGrey800 !important;
}

.menuItemsContainer {
  list-style: none;
  margin: 0;
  padding: 8px 0;
}

.menuLabel {
  border-bottom: 1px solid tealLight1;
  border-top: 2px solid #f0f0ee;
  color: gradientDark4 !important;
  font-size: 0.88rem;
  font-weight: 600;
  margin: -2px 0 0;
  padding: 10px 0 8px 15px;
}

.menuLabel span {
  font-style: italic;
  font-weight: 400;
}

.menuItemIcon {
  display: inline-block;
  margin-right: 8px;
  vertical-align: -30%;
}

.menuItemSubLabel,
.menuItemSelectedText {
  display: inline-block;
  vertical-align: baseline;
}

.menuItemLabel {
  max-width: 700px;
  overflow-x: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.menuItemSubLabel {
  font-size: 10px;
  margin-left: 2px;
}

.menuItemSelectedText {
  color: jcGrey800;
  font-size: 0.85rem;
  line-height: 18px;
  margin-left: 20px;
}

.externalLinkIcon {
  height: 0.75rem;
  margin-left: 0.5rem;
}

.externalLinkIcon path {
  fill: gradientDark2;
}

.menuItem.includesIcon:hover .externalLinkIcon path {
  fill: jcGrey800;
}

.menuItemSeparator {
  background-color: gradientLight3;
  height: 1px;
  margin: 9px 0;
  overflow: hidden;
}

.searchContext {
  left: 0;
  max-width: 350px;
  width: 100%;
}

.changeStateDropDownList {
  left: 16px;
  margin-bottom: 7px;
}

.statusDropDownList {
  right: -20px;
}

@media (max-width: max-width-medium-large) {
  .dropDownMenuList { max-width: 500px; }
  .menuItemLabel { max-width: 400px; }
}

@media (max-width: max-width-medium) {
  .dropDownMenuList { max-width: 350px; }
  .menuItemLabel { max-width: 250px; }
}
</style>
